/**
 * DiagnosisUnlockFlow DEBUG / Plan stub 用完整 payload mock。
 *
 * 结构对齐 `present_risk_and_goals` SSE（含后端注入的 `goalDraft.goalId`）。
 * **不在弹窗内请求接口** — 调用方负责传入。
 */

import type { DiagnosisUnlockModalPayload } from "../types";

/** Onboarding / Chat 链路 — 与 Figma Step2/3 文案接近 */
export const MOCK_ONBOARDING_RISK_PAYLOAD: DiagnosisUnlockModalPayload = {
  riskSummary:
    "You're in okay shape, but a few things stand out in your portfolio.",
  riskItems: [
    {
      title: "Portfolio volatility is high",
      description: "Current portfolio historical volatility 18%",
      riskLevel: "high",
    },
    {
      title: "Portfolio concentration too high",
      description: "NVDA accounts for 52%, with higher single-stock risk.",
      riskLevel: "medium",
    },
    {
      title: "Cash allocation is slightly low",
      description: "Emergency fund covers 3.2 months — target is 6 months.",
      riskLevel: "low",
    },
  ],
  goalDraft: {
    goalId: "mock-goal-onboarding-001",
    title: "Put you idle cash to work",
    summary:
      "Pings when one of your top holdings changes rank in your book — early signal that concentration is creeping back.",
    trackerSpecs: [
      { title: "Rank movemnet" },
      { title: "Earnings report" },
      { title: "Ed' Pick" },
      { title: "Technical signals" },
    ],
  },
};

/** Plan tax checkup — 未设立 Goal → Step3「Setup goal」 */
export const MOCK_PLAN_REPORT_NO_GOAL_PAYLOAD: DiagnosisUnlockModalPayload = {
  riskSummary:
    "You've got unused tax-advantaged room worth about $2,800 in tax this year.",
  riskItems: [
    {
      title: "Unused tax-advantaged room",
      description:
        "$14,300 of tax-advantaged contribution room left — about $2,800 in tax this year.",
      riskLevel: "high",
    },
    {
      title: "HSA not maxed out",
      description: "You're $2,100 below the annual HSA limit.",
      riskLevel: "medium",
    },
    {
      title: "Withholding may be slightly high",
      description: "Estimated refund suggests ~$400/month could move to cash.",
      riskLevel: "low",
    },
  ],
  goalDraft: {
    goalId: "mock-goal-plan-tax-001",
    title: "Max out tax-advantaged accounts",
    summary:
      "Track contribution room across 401(k), IRA, and HSA so you don't leave tax savings on the table.",
    trackerSpecs: [
      { title: "401(k) contribution pace" },
      { title: "HSA room remaining" },
      { title: "Tax-loss harvest signals" },
    ],
  },
};

/** Plan cash checkup — 已设立 Goal → Step3「View my goal」 */
export const MOCK_PLAN_REPORT_HAS_GOAL_PAYLOAD: DiagnosisUnlockModalPayload = {
  riskSummary:
    "Your cash is working, but there's room to optimize idle balances.",
  riskItems: [
    {
      title: "Emergency fund below target",
      description: "3.2 months of expenses covered — target is 6 months.",
      riskLevel: "high",
    },
    {
      title: "Idle cash in low-yield accounts",
      description: "$28,400 earning under 1% APY.",
      riskLevel: "medium",
    },
    {
      title: "Savings rate is steady",
      description: "You're saving 12% of take-home pay each month.",
      riskLevel: "low",
    },
  ],
  goalDraft: {
    goalId: "mock-goal-plan-cash-existing",
    title: "Put you idle cash to work",
    summary:
      "Move excess cash into higher-yield vehicles while keeping your emergency buffer intact.",
    trackerSpecs: [
      { title: "High-yield account rate" },
      { title: "Cash runway months" },
      { title: "Monthly savings rate" },
    ],
  },
};

/** Plan Completed 卡 `cardId` → payload + 是否已有 Goal（决定 Step3 CTA） */
const PLAN_CHECKUP_PAYLOAD_BY_CARD: Record<
  string,
  { payload: DiagnosisUnlockModalPayload; hasEstablishedGoal: boolean }
> = {
  "completed-tax": {
    payload: MOCK_PLAN_REPORT_NO_GOAL_PAYLOAD,
    hasEstablishedGoal: false,
  },
  "completed-cash": {
    payload: MOCK_PLAN_REPORT_HAS_GOAL_PAYLOAD,
    hasEstablishedGoal: true,
  },
};

export function getPlanCheckupPayloadMock(checkupCardId: string): {
  payload: DiagnosisUnlockModalPayload;
  hasEstablishedGoal: boolean;
} {
  return (
    PLAN_CHECKUP_PAYLOAD_BY_CARD[checkupCardId] ?? {
      payload: MOCK_PLAN_REPORT_NO_GOAL_PAYLOAD,
      hasEstablishedGoal: false,
    }
  );
}
